import { useAuthSession } from '@modeling/account';
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { Spin } from 'antd';
import { adminScopeApi } from '../api/services/adminScopeApi';
import type { AdminScope } from '../types/adminScope';

interface AdminScopeContextValue {
  scope: AdminScope | null;
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
}

const AdminScopeContext = createContext<AdminScopeContextValue | null>(null);

const EMPTY_SCOPE: AdminScope = {
  isPlatformAdmin: false,
  managedTenantIds: [],
  canAccessDcAdmin: false,
};

export function AdminScopeProvider({ children }: { children: React.ReactNode }) {
  const session = useAuthSession();
  const initialized = session.initialized;
  const [scope, setScope] = useState<AdminScope | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    if (!session.authenticated) {
      setScope(null);
      setError(null);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      setScope(await adminScopeApi.getMyScope());
    } catch (err) {
      setScope(EMPTY_SCOPE);
      setError(err instanceof Error ? err.message : '加载管理权限失败');
    } finally {
      setLoading(false);
    }
  }, [session.authenticated]);

  useEffect(() => {
    if (initialized && session.authenticated) {
      refresh();
    } else if (initialized) {
      setScope(null);
    }
  }, [initialized, session.authenticated, session.token, refresh]);

  const value = useMemo(
    () => ({ scope, loading, error, refresh }),
    [scope, loading, error, refresh],
  );

  if (initialized && session.authenticated && loading && !scope) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <Spin size="large" tip="正在加载管理权限..." />
      </div>
    );
  }

  return <AdminScopeContext.Provider value={value}>{children}</AdminScopeContext.Provider>;
}

export function useAdminScope(): AdminScopeContextValue {
  const ctx = useContext(AdminScopeContext);
  if (!ctx) {
    throw new Error('useAdminScope must be used within AdminScopeProvider');
  }
  return ctx;
}
